import { useMemo , useCallback} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Your existing useUserInfo hook
export function useUserInfo() {
  const user = useMemo(() => {
    try {
      const orgData = localStorage.getItem('userState');
      if (!orgData) return null;
      
      const parsed = JSON.parse(orgData);
      return parsed?.user || null;
    } catch (err) {
      console.error('Failed to parse userState from localStorage:', err);
      return null;
    }
  }, []);
  
  const orgId = user?.organizationId || null;
  const orgName = user?.name || '';
  const orgEmail = user?.email || '';
  const userId = user?.id || null;
  const role = user?.role || '';
  const roleId = user?.roleId || null;
  
  return {
    user,
    orgId,
    orgName,
    orgEmail,
    userId,
    role,
    roleId,
  };
}

// Enhanced hook that gets orgId from multiple sources
export function useOrgId() {
  const location = useLocation();
  const { orgId: userInfoOrgId } = useUserInfo();
  
  const orgId = useMemo(() => {
    try {
      // Method 1: Try to get from URL search params
      const searchParams = new URLSearchParams(location.search);
      const urlOrgId = searchParams.get('org');
      
      if (urlOrgId && !isNaN(parseInt(urlOrgId))) {
        console.log('OrgId found in URL params:', urlOrgId);
        return parseInt(urlOrgId);
      }
      
      // Method 2: Try to get from localStorage via useUserInfo hook
      if (userInfoOrgId && !isNaN(parseInt(userInfoOrgId))) {
        // console.log('OrgId found in localStorage:', userInfoOrgId);
        return parseInt(userInfoOrgId);
      }
      
      // Method 3: Fallback - try to parse from current pathname if it follows a pattern
      const pathOrgMatch = location.pathname.match(/[?&]org=(\d+)/);
      if (pathOrgMatch && pathOrgMatch[1]) {
        console.log('OrgId found in pathname:', pathOrgMatch[1]);
        return parseInt(pathOrgMatch[1]);
      }
      
      console.warn('No orgId found in URL params or localStorage');
      return null;
    } catch (error) {
      console.error('Error resolving orgId:', error);
      return null;
    }
  }, [location.search, location.pathname, userInfoOrgId]);
  
  return orgId;
}

// Stop redirect handler
export function useStopRedirect() {
  const navigate = useNavigate();
  const orgId = useOrgId();

  
  const redirectToStopTrack = (stopId) => {
    try {
      if (!stopId) {
        console.error('Stop ID is required for redirect');
        return false;
      }
      
      if (!orgId) {
        console.error('Organization ID not found. Cannot redirect to track screen.');
        // You might want to show a toast notification here
        return false;
      }
      
      const trackUrl = `/track/screen/${stopId}?org=${orgId}`;
      console.log('Redirecting to:', trackUrl);
      
      navigate(trackUrl);
      return true;
    } catch (error) {
      console.error('Error redirecting to stop track:', error);
      return false;
    }
  };
  
  return { redirectToStopTrack, orgId };
}

// Alternative utility function if you prefer not to use hooks
export const getOrgIdFromMultipleSources = () => {
  try {
    // Method 1: Try URL search params from current location
    const urlParams = new URLSearchParams(window.location.search);
    const urlOrgId = urlParams.get('org');
    
    if (urlOrgId && !isNaN(parseInt(urlOrgId))) {
      return parseInt(urlOrgId);
    }
    
    // Method 2: Try localStorage
    const userStateString = localStorage.getItem('userState');
    if (userStateString) {
      const userState = JSON.parse(userStateString);
      const orgId = userState?.user?.organizationId;
      
      if (orgId && !isNaN(parseInt(orgId))) {
        return parseInt(orgId);
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting orgId from multiple sources:', error);
    return null;
  }
};

export const useInfoSide=()=>{
     const urlParams = new URLSearchParams(window.location.search);
    const InfoSide = urlParams.get('InfoSide') || "right";
    
    if (InfoSide) {
      return InfoSide;
    }else{
      return null;
    }

    
}

// Enhanced redirect function that works with React Router (recommended for multi-tenant)
export const useStopRedirectEnhanced = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orgId = useOrgId();
  
  const redirectToStopTrack = useCallback((stopId) => {
    try {
      if (!stopId) {
        console.error('Stop ID is required for redirect');
        return false;
      }
      
      if (!orgId) {
        console.error('Organization ID not found. Cannot redirect to track screen.');
        // Redirect to dashboard instead of hardcoded URL
        navigate('/dashboard');
        return false;
      }
      
      // Use relative path for multi-tenant compatibility
      const trackPath = `/track/screen/${stopId}?org=${orgId}`;
      console.log('Navigating to:', trackPath);
      
      navigate(trackPath);
      return true;
    } catch (error) {
      console.error('Error redirecting to stop track:', error);
      // Fallback to dashboard
      navigate('/dashboard');
      return false;
    }
  }, [navigate, orgId]);
  
  return { redirectToStopTrack, orgId };
};

// Standalone redirect function (fallback for non-hook usage)
export const redirectToStopTrack = (stopId, customOrgId = null, navigate = null) => {
  try {
    if (!stopId) {
      throw new Error('Stop ID is required');
    }
    
    const orgId = customOrgId || getOrgIdFromMultipleSources();
    
    if (!orgId) {
      console.error('Organization ID not found');
      // Use relative path for fallback
      if (navigate) {
        navigate('/dashboard');
      } else {
        // Only use window.location as last resort with relative path
        window.location.href = '/dashboard';
      }
      return false;
    }
    
    // Use relative path for multi-tenant compatibility
    const trackPath = `/track/screen/${stopId}?org=${orgId}`;
    
    if (navigate) {
      // Prefer React Router navigation
      navigate(trackPath);
    } else {
      // Fallback to window.location with relative path
      window.location.href = trackPath;
    }
    
    return true;
  } catch (error) {
    console.error('Error in redirectToStopTrack:', error);
    
    // Fallback navigation
    if (navigate) {
      navigate('/dashboard');
    } else {
      window.location.href = '/dashboard';
    }
    return false;
  }
};

// Example usage in a component (recommended approach)
export const StopListComponent = () => {
  const { redirectToStopTrack, orgId } = useStopRedirectEnhanced();
  
  const handleStopClick = (stopId) => {
    const success = redirectToStopTrack(stopId);
    
    if (!success) {
      // Handle error case - maybe show a toast notification
      console.error('Failed to redirect to stop track');
    }
  };
  
  return (
    <div>
      <p>Current Org ID: {orgId || 'Not found'}</p>
      <button onClick={() => handleStopClick(98)}>
        Go to Stop 98
      </button>
    </div>
  );
};

// Multi-tenant safe redirect function
export const handleStopRedirectWithFallback = (stopId, navigate = null) => {
  try {
    // First try to get orgId from URL
    const urlParams = new URLSearchParams(window.location.search);
    let orgId = urlParams.get('org');
    
    // If not in URL, try localStorage
    if (!orgId) {
      const userState = JSON.parse(localStorage.getItem('userState') || '{}');
      orgId = userState?.user?.organizationId;
    }
    
    // If still no orgId, redirect to dashboard
    if (!orgId) {
      console.error('No organization ID found. Redirecting to dashboard...');
      if (navigate) {
        navigate('/dashboard');
      } else {
        window.location.href = '/dashboard';
      }
      return false;
    }
    
    // Use relative path for multi-tenant support
    const trackPath = `/track/screen/${stopId}?org=${orgId}`;
    
    if (navigate) {
      navigate(trackPath);
    } else {
      window.location.href = trackPath;
    }
    
    return true;
    
  } catch (error) {
    console.error('Error handling stop redirect:', error);
    
    // Fallback: redirect to dashboard with relative path
    if (navigate) {
      navigate('/dashboard');
    } else {
      window.location.href = '/dashboard';
    }
    return false;
  }
};